// Instance Admin → Edge & HTTPS: the PROPAGATION view (every project whose
// domain changes have not reached every server) and the DELIVERY settings
// (the hub's scheduler, retries, offline detection and alert emails).

import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { RefreshCw } from 'lucide-react'
import {
  getEdgePropagation, getEdgeSchedule, updateEdgeSchedule,
  type EdgePropagation, type EdgeSchedule,
} from '@/api/instanceAdmin'
import { Field, InfoBox, Td, Th, chipStyle, errText, tableStyle } from './adminUi'
import { SERVER_STATUS, fmtTime } from './EdgeServersPanel'

export function EdgePropagationPanel() {
  const [p, setP] = useState<EdgePropagation | null>(null)
  const refresh = useCallback(async () => {
    try { setP(await getEdgePropagation()) } catch (e) {
      toast.error(errText(e, 'Failed to load propagation'))
    }
  }, [])
  useEffect(() => {
    void refresh()
    const t = window.setInterval(() => { void refresh() }, 15000)
    return () => window.clearInterval(t)
  }, [refresh])

  if (!p) return <div style={{ color: 'var(--color-text-secondary)' }}>Loading…</div>
  const behind = p.servers.filter((s) => s.enabled && !s.in_sync)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <span style={chipStyle}>current revision {p.revision}</span>
        <span style={{
          ...chipStyle,
          color: behind.length ? 'var(--color-error)' : 'var(--color-success)',
        }}>
          {behind.length ? `${behind.length} server(s) behind` : 'Every server is in sync'}
        </span>
        <button className="btn btn-secondary btn-sm" onClick={() => void refresh()} title="Refresh">
          <RefreshCw size={13} />
        </button>
      </div>

      {behind.length > 0 && (
        <table style={tableStyle}>
          <thead><tr><Th>Server</Th><Th>Status</Th><Th>Applied</Th><Th>Out of sync since</Th><Th>Reason</Th></tr></thead>
          <tbody>
            {behind.map((s) => {
              const st = SERVER_STATUS[s.status] || SERVER_STATUS.new
              return (
                <tr key={s.id}>
                  <Td>{s.name}</Td>
                  <Td><span style={{ ...chipStyle, color: st.color }}>{st.label}</span></Td>
                  <Td>{s.applied_revision} / {s.current_revision}</Td>
                  <Td>{fmtTime(s.failing_since)}{s.alerted_at ? ' · alert sent' : ''}</Td>
                  <Td style={{ maxWidth: 360 }}>
                    <span style={{ fontSize: '0.8125rem', color: 'var(--color-error)' }}>{s.last_error || '—'}</span>
                  </Td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}

      <div>
        <h4 style={{ fontSize: '0.9625rem', margin: '0 0 0.25rem' }}>Projects affected</h4>
        {p.affected_projects.length === 0 ? (
          <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', margin: 0 }}>
            No project has an undelivered domain or certificate change.
          </p>
        ) : (
          <table style={tableStyle}>
            <thead><tr><Th>Project</Th><Th>Domains</Th><Th>Missing on</Th><Th>Since</Th><Th>Changes</Th></tr></thead>
            <tbody>
              {p.affected_projects.map((a) => (
                <tr key={`${a.owner}/${a.project}`}>
                  <Td><strong>{a.project}</strong></Td>
                  <Td>{a.hostnames.join(', ') || '—'}</Td>
                  <Td>{a.servers.join(', ')}</Td>
                  <Td>{fmtTime(a.since)}</Td>
                  <Td>
                    <ul style={{ margin: 0, paddingLeft: '1rem', fontSize: '0.8125rem' }}>
                      {a.changes.map((c, i) => <li key={i}>{c}</li>)}
                    </ul>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

export function EdgeSchedulePanel() {
  const [s, setS] = useState<EdgeSchedule | null>(null)
  const [busy, setBusy] = useState(false)
  useEffect(() => {
    getEdgeSchedule().then(setS).catch((e) => toast.error(errText(e, 'Failed to load')))
  }, [])
  if (!s) return <div style={{ color: 'var(--color-text-secondary)' }}>Loading…</div>

  const set = <K extends keyof EdgeSchedule>(k: K, v: EdgeSchedule[K]) => setS({ ...s, [k]: v })
  const num = (k: keyof EdgeSchedule, label: string, hint: string, unit: string) => (
    <Field label={`${label} (${unit})`} hint={hint}>
      <input type="number" min={1} value={s[k] as number}
        onChange={(e) => set(k, Number(e.target.value) as never)} style={{ width: 160 }} />
    </Field>
  )
  const save = async () => {
    setBusy(true)
    try {
      setS(await updateEdgeSchedule(s))
      toast.success('Delivery settings saved')
    } catch (e) {
      toast.error(errText(e, 'Save failed'))
    } finally { setBusy(false) }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.9rem', maxWidth: 640 }}>
      <InfoBox>
        Every change is delivered to every server <strong>immediately</strong>, whether or not
        the scheduler is on. The scheduler is the safety net: it retries failed deliveries,
        checks that agents are alive, re-sends to an agent that came back empty, and marks
        servers that stop answering as offline.
      </InfoBox>

      <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: '0.9375rem' }}>
        <input type="checkbox" checked={s.enabled} onChange={(e) => set('enabled', e.target.checked)} />
        Run the scheduler (retries, health checks, offline detection)
      </label>
      {!s.enabled && (
        <InfoBox>
          With the scheduler off, a delivery that fails is <strong>not</strong> retried until you
          press <em>Push now</em> on that server. Alerts are still evaluated.
        </InfoBox>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: '0.75rem' }}>
        {num('interval_s', 'Run every', 'How often retries and health checks run.', 'seconds')}
        {num('push_timeout_s', 'Push timeout', 'How long one call to an agent may take.', 'seconds')}
        {num('retry_base_s', 'First retry after', 'Each further retry waits twice as long…', 'seconds')}
        {num('retry_max_s', '…up to at most', 'The longest wait between retries.', 'seconds')}
        {num('offline_after_s', 'Offline after', 'A pull agent that has not polled for this long is offline.', 'seconds')}
      </div>

      <h4 style={{ fontSize: '0.9625rem', margin: '0.5rem 0 0' }}>Alerts</h4>
      {num('alert_after_min', 'Alert when out of sync for', 'One email per incident, and another when the server recovers.', 'minutes')}
      <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: '0.9375rem' }}>
        <input type="checkbox" checked={s.alert_admins} onChange={(e) => set('alert_admins', e.target.checked)} />
        Email instance admins (every server, every project)
      </label>
      <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: '0.9375rem' }}>
        <input type="checkbox" checked={s.alert_managers} onChange={(e) => set('alert_managers', e.target.checked)} />
        Email the project managers of affected projects (their own domains only)
      </label>
      <Field label="Also email" hint="Comma-separated, e.g. an operations mailbox.">
        <input value={s.alert_extra_emails} onChange={(e) => set('alert_extra_emails', e.target.value)}
          placeholder="ops@yourcompany.com" style={{ width: '100%' }} />
      </Field>

      <div>
        <button className="btn btn-primary" onClick={save} disabled={busy}>
          {busy ? 'Saving…' : 'Save delivery settings'}
        </button>
      </div>
    </div>
  )
}
